import React, { useEffect, useRef, useCallback, useState } from 'react';
import maplibregl from 'maplibre-gl';
import ReactDOM from 'react-dom/client';
import ParagliderIcon from '../components/ParagliderIcon';
import { PilotData } from '../types';

interface UsePilotMarkersOptions {
  map: maplibregl.Map | null;
  pilots: Map<string, PilotData>;
  followingPilot: string | null;
  onFirstMarker?: () => void;
  is3D?: boolean; // Hide 2D markers when in 3D mode
}

export const usePilotMarkers = ({
  map,
  pilots,
  followingPilot,
  onFirstMarker,
  is3D = false
}: UsePilotMarkersOptions) => {
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const markerRootsRef = useRef<Map<string, ReactDOM.Root>>(new Map());
  const hasFirstMarker = useRef(false);
  const [markersState, setMarkersState] = useState<Map<string, maplibregl.Marker>>(new Map());
  const currentZoomRef = useRef<number>(12);

  // Calculate scale based on zoom level
  const getScaleForZoom = useCallback((zoom: number) => {
    // Scale down as we zoom out
    // At zoom 16+: full size (1.0)
    // At zoom 14: 0.8
    // At zoom 12: 0.6
    // At zoom 10: 0.4
    // At zoom 8-: 0.3 minimum
    if (zoom >= 16) return 1.0;
    if (zoom >= 14) return 0.8;
    if (zoom >= 12) return 0.6;
    if (zoom >= 10) return 0.4;
    return 0.3;
  }, []);

  // Update all marker scales
  const updateMarkerScales = useCallback((zoom: number) => {
    const scale = getScaleForZoom(zoom);
    markersRef.current.forEach((marker, pilotId) => {
      const markerElement = marker.getElement();
      const iconElement = markerElement.querySelector('.pilot-icon') as HTMLElement;

      if (iconElement) {
        // Get current rotation from the pilot data
        const pilot = pilots.get(pilotId);
        const rotation = pilot?.heading || 0;
        // Apply both rotation and zoom-based scale
        iconElement.style.transform = `rotate(${rotation}deg) scale(${scale})`;
      }

      // Label is absolutely positioned, no need to adjust
    });
  }, [getScaleForZoom, pilots]);

  // Clean up markers for pilots that no longer exist
  const cleanupRemovedPilots = useCallback(() => {
    const currentPilotIds = new Set(Array.from(pilots.keys()));

    markersRef.current.forEach((marker, pilotId) => {
      if (!currentPilotIds.has(pilotId)) {
        // Remove marker from map
        marker.remove();
        markersRef.current.delete(pilotId);

        // Clean up React root - use setTimeout to avoid React warning
        const root = markerRootsRef.current.get(pilotId);
        if (root) {
          setTimeout(() => {
            root.unmount();
            markerRootsRef.current.delete(pilotId);
          }, 0);
        }

      }
    });
  }, [pilots]);

  // Update or create markers
  useEffect(() => {
    if (!map) return;

    // Skip all marker operations in 3D mode
    if (is3D) {
      // Only remove markers if they exist
      if (markersRef.current.size > 0) {
        markersRef.current.forEach((marker) => {
          marker.remove();
        });
        markersRef.current.clear();

        // Clean up React roots
        markerRootsRef.current.forEach((root) => {
          setTimeout(() => root.unmount(), 0);
        });
        markerRootsRef.current.clear();

        setMarkersState(new Map());
      }
      return;
    }

    // Clean up removed pilots first
    cleanupRemovedPilots();

    // Update or create markers for each pilot
    pilots.forEach((pilot) => {
      const existingMarker = markersRef.current.get(pilot.id);

      if (existingMarker) {
        // Update existing marker position
        const currentLngLat = existingMarker.getLngLat();
        if (currentLngLat.lng !== pilot.lng || currentLngLat.lat !== pilot.lat) {
          existingMarker.setLngLat([pilot.lng, pilot.lat]);
        }

        // Update icon rotation based on heading while preserving zoom scale
        // Only rotate the icon container, not the label
        const markerElement = existingMarker.getElement();
        const iconElement = markerElement.querySelector('.pilot-icon') as HTMLElement;
        if (iconElement && pilot.heading !== undefined && pilot.heading !== null) {
          // Apply rotation and current zoom scale to the icon container
          const currentScale = getScaleForZoom(map ? map.getZoom() : 12);
          iconElement.style.transform = `rotate(${pilot.heading}deg) scale(${currentScale})`;
        }

        // Update the label text and position
        const labelElement = markerElement.querySelector('.pilot-label') as HTMLElement;
        if (labelElement) {
          labelElement.innerHTML = `
            <div class="pilot-name">${pilot.name}</div>
            <div class="pilot-altitude">${Math.round(pilot.altitude)}m</div>
          `;

        }

        // Update the React component
        const root = markerRootsRef.current.get(pilot.id);
        if (root) {
          root.render(
            <ParagliderIcon
              color={pilot.color}
              size={60}
            />
          );
        }
      } else {
        // Create new marker
        const el = document.createElement('div');
        el.className = 'pilot-marker';
        el.style.cursor = 'pointer';
        el.style.zIndex = '1000';  // High z-index to ensure marker is above tracks
        el.style.position = 'relative';  // Ensure z-index works properly

        // Create container with fixed size for icon
        const container = document.createElement('div');
        container.style.width = '60px';
        container.style.height = '60px';
        container.style.position = 'relative';

        // Create icon container with rotation and zoom scale
        const iconContainer = document.createElement('div');
        iconContainer.className = 'pilot-icon';
        iconContainer.style.width = '60px';
        iconContainer.style.height = '60px';
        iconContainer.style.transition = 'transform 0.3s ease';
        // Apply initial rotation and zoom scale
        const initialRotation = pilot.heading || 0;
        const initialScale = getScaleForZoom(map ? map.getZoom() : 12);
        iconContainer.style.transform = `rotate(${initialRotation}deg) scale(${initialScale})`;
        iconContainer.style.transformOrigin = 'center center';

        // Create label positioned absolutely to the right
        const label = document.createElement('div');
        label.className = 'pilot-label';
        label.style.position = 'absolute';
        label.style.left = '100%';
        label.style.top = '50%';
        label.style.transform = 'translateY(-50%)';
        label.style.marginLeft = '5px';
        label.style.whiteSpace = 'nowrap';
        label.innerHTML = `
          <div class="pilot-name">${pilot.name}</div>
          <div class="pilot-altitude">${Math.round(pilot.altitude)}m</div>
        `;

        container.appendChild(iconContainer);
        container.appendChild(label);
        el.appendChild(container);

        // Create React root and render icon in the icon container
        const root = ReactDOM.createRoot(iconContainer);
        root.render(
          <ParagliderIcon
            color={pilot.color}
            size={60}
          />
        );

        // Store the root for later updates
        markerRootsRef.current.set(pilot.id, root);

        // Create the marker WITHOUT rotation (rotation applied to icon only)
        // Use 'center' anchor to position the paraglider center on the exact coordinates
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'center',  // Center anchor positions the center of the element at the coordinates
          rotationAlignment: 'map',
          pitchAlignment: 'map'
          // No rotation here - applied to icon element only
        })
          .setLngLat([pilot.lng, pilot.lat])
          .addTo(map);

        markersRef.current.set(pilot.id, marker);

        // Track first marker creation
        if (!hasFirstMarker.current && onFirstMarker) {
          hasFirstMarker.current = true;
          onFirstMarker();
        }
      }
    });

    // Only update state if markers actually changed and we're in 2D mode
    if (!is3D) {
      setMarkersState(prevState => {
        if (markersRef.current.size !== prevState.size ||
            Array.from(markersRef.current.keys()).some(key => !prevState.has(key))) {
          return new Map(markersRef.current);
        }
        return prevState;
      });
    }
  }, [map, pilots, cleanupRemovedPilots, onFirstMarker, getScaleForZoom, is3D]);

  // Handle zoom changes
  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      const zoom = map.getZoom();
      currentZoomRef.current = zoom;
      updateMarkerScales(zoom);
    };

    // Set initial zoom
    handleZoom();

    // Listen for zoom changes
    map.on('zoom', handleZoom);

    return () => {
      map.off('zoom', handleZoom);
    };
  }, [map, updateMarkerScales]);

  // Follow pilot if selected
  useEffect(() => {
    if (!map || !followingPilot) return;

    const pilot = pilots.get(followingPilot);
    if (pilot) {
      map.easeTo({
        center: [pilot.lng, pilot.lat],
        duration: 1000
      });
    }
  }, [map, pilots, followingPilot]);

  // Cleanup on unmount
  useEffect(() => {
    // Capture refs in local variables to avoid stale closure issues
    const markers = markersRef.current;
    const markerRoots = markerRootsRef.current;

    return () => {
      // Clean up all markers and React roots
      markers.forEach((marker) => {
        marker.remove();
      });
      markers.clear();

      // Defer React root unmounting to avoid warnings
      const rootsToUnmount = Array.from(markerRoots.values());
      markerRoots.clear();

      if (rootsToUnmount.length > 0) {
        setTimeout(() => {
          rootsToUnmount.forEach(root => {
            try {
              root.unmount();
            } catch (error) {
              // Ignore errors if already unmounted
            }
          });
        }, 0);
      }
    };
  }, []);

  return {
    markers: markersState,  // Return the state, not the ref
    hasMarkers: markersState.size > 0
  };
};